import { useEffect, useRef } from "react";
import { hasEnoughContentForAds } from "@/lib/ad-guard";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

const AD_CLIENT = "ca-pub-7486483683839417";

interface AdSlotProps {
  /** Google AdSense ad-unit slot id. Leave unset to use a responsive
   *  in-article/display unit created in the AdSense dashboard later —
   *  this component still reserves the layout position now. */
  slotId?: string;
  /** The page's real content, concatenated. Used to enforce the
   *  thin-content guard: no ad slot renders unless there's substantive
   *  text around it. */
  content: (string | undefined | null)[];
  className?: string;
  format?: "auto" | "fluid";
}

/**
 * A reserved container for a manual AdSense unit (e.g. in-content /
 * in-article ads), separate from the sitewide Auto/Anchor ads script in
 * index.html. Renders nothing — not even an empty placeholder — on pages
 * whose surrounding content is too thin, so ads never end up next to
 * near-empty states.
 */
export function AdSlot({ slotId, content, className, format = "auto" }: AdSlotProps) {
  const ref = useRef<HTMLModElement>(null);
  const allowed = hasEnoughContentForAds(...content);

  useEffect(() => {
    if (!allowed) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // AdSense script may not be loaded yet (e.g. ad blocker) — fail silently,
      // the reserved slot simply stays empty rather than breaking the page.
    }
  }, [allowed]);

  if (!allowed) return null;

  return (
    <div className={className} data-testid="ad-slot">
      <ins
        ref={ref}
        className="adsbygoogle block"
        style={{ display: "block" }}
        data-ad-client={AD_CLIENT}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}
